import React, { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi, alertsApi, seedApi, farmsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import FarmMap from '../components/map/FarmMap';
import {
  MapPin,
  Grid3X3,
  Radio,
  Bell,
  AlertTriangle,
  CheckCircle2,
  Wifi,
  WifiOff,
  RefreshCw,
  Database,
  TrendingUp,
  Activity,
  Loader2,
  Map,
} from 'lucide-react';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const { socket } = useSocket();
  const { isSuperAdmin } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, alertsRes, farmsRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getRecentAlerts(),
        farmsApi.getAll(),
      ]);
      setStats(statsRes.data);
      setRecentAlerts(alertsRes.data);
      setFarms(farmsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates
  useEffect(() => {
    if (!socket) return;

    socket.on('new_alert', (data) => {
      toast.warning(`Nouvelle alerte: ${data.title}`, {
        description: data.message,
      });
      fetchData();
    });

    socket.on('alert_acknowledged', () => {
      fetchData();
    });

    socket.on('alert_resolved', () => {
      fetchData();
    });

    socket.on('sensor_status_change', () => {
      fetchData();
    });

    return () => {
      socket.off('new_alert');
      socket.off('alert_acknowledged');
      socket.off('alert_resolved');
      socket.off('sensor_status_change');
    };
  }, [socket, fetchData]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedApi.seed();
      toast.success('Base de données initialisée avec succès');
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'initialisation");
    } finally {
      setSeeding(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await alertsApi.acknowledge(alertId);
      toast.success('Alerte acquittée');
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'acquittement");
    }
  };

  const severityColors = {
    critical: 'bg-red-500 text-white',
    warning: 'bg-amber-500 text-white',
    info: 'bg-blue-500 text-white',
  };

  const severityLabels = {
    critical: 'Critique',
    warning: 'Attention',
    info: 'Info',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Tableau de bord</h1>
          <p className="text-muted-foreground mt-1">Vue d'ensemble de votre exploitation</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualiser
          </Button>
          {isSuperAdmin && (
            <Button onClick={handleSeed} disabled={seeding} data-testid="seed-btn">
              {seeding ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Database className="mr-2 h-4 w-4" />
              )}
              Initialiser les données
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards - Bento Grid */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {/* Farms */}
        <Card className="card-hover" data-testid="kpi-farms">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Exploitations
            </CardTitle>
            <MapPin className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.farms || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Exploitations actives
            </p>
          </CardContent>
        </Card>

        {/* Parcels */}
        <Card className="card-hover" data-testid="kpi-parcels">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Parcelles
            </CardTitle>
            <Grid3X3 className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.parcels || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Parcelles surveillées
            </p>
          </CardContent>
        </Card>

        {/* Sensors */}
        <Card className="card-hover" data-testid="kpi-sensors">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Capteurs
            </CardTitle>
            <Radio className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.sensors?.total || 0}</div>
            <div className="flex items-center gap-3 mt-1">
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <Wifi className="h-3 w-3" />
                {stats?.sensors?.online || 0} en ligne
              </span>
              <span className="flex items-center gap-1 text-xs text-red-500">
                <WifiOff className="h-3 w-3" />
                {stats?.sensors?.offline || 0} hors ligne
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="card-hover" data-testid="kpi-alerts">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Alertes actives
            </CardTitle>
            <Bell className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.alerts?.active || 0}</div>
            {stats?.alerts?.critical > 0 && (
              <p className="flex items-center gap-1 text-xs text-red-500 mt-1">
                <AlertTriangle className="h-3 w-3" />
                {stats.alerts.critical} critique(s)
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Map Section */}
      <Card data-testid="farms-map-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Map className="h-5 w-5 text-primary" />
            Carte des exploitations
          </CardTitle>
          <CardDescription>
            Localisation géographique de vos exploitations agricoles
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FarmMap 
            farms={farms} 
            height="500px"
            onFarmClick={(farm) => toast.info(`${farm.name}`, { description: farm.address })}
          />
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-12">
        {/* Sensor Status Overview */}
        <Card className="lg:col-span-8" data-testid="sensor-overview">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              État des capteurs
            </CardTitle>
            <CardDescription>
              Répartition des capteurs par statut
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {/* Online */}
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/20 p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <div className="h-3 w-3 rounded-full bg-emerald-500 animate-pulse-live" />
                </div>
                <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">
                  {stats?.sensors?.online || 0}
                </div>
                <p className="text-sm text-emerald-600 dark:text-emerald-500 mt-1">En ligne</p>
              </div>

              {/* Offline */}
              <div className="rounded-xl bg-red-50 dark:bg-red-950/20 p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                </div>
                <div className="text-3xl font-bold text-red-700 dark:text-red-400">
                  {stats?.sensors?.offline || 0}
                </div>
                <p className="text-sm text-red-600 dark:text-red-500 mt-1">Hors ligne</p>
              </div>

              {/* Total */}
              <div className="rounded-xl bg-slate-100 dark:bg-slate-800 p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <TrendingUp className="h-4 w-4 text-slate-500" />
                </div>
                <div className="text-3xl font-bold text-slate-700 dark:text-slate-300">
                  {stats?.sensors?.total || 0}
                </div>
                <p className="text-sm text-slate-500 mt-1">Total</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Taux de disponibilité</span>
                <span className="font-medium">
                  {stats?.sensors?.total > 0
                    ? Math.round((stats.sensors.online / stats.sensors.total) * 100)
                    : 0}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{
                    width: `${stats?.sensors?.total > 0 ? (stats.sensors.online / stats.sensors.total) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="lg:col-span-4" data-testid="recent-alerts">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-destructive" />
              Alertes récentes
            </CardTitle>
            <CardDescription>
              {recentAlerts.length} alerte(s) active(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              {recentAlerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
                  <p className="text-muted-foreground">Aucune alerte active</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`rounded-lg p-3 ${
                        alert.severity === 'critical'
                          ? 'alert-critical'
                          : alert.severity === 'warning'
                          ? 'alert-warning'
                          : 'alert-info'
                      }`}
                      data-testid={`alert-${alert.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge
                              className={`${severityColors[alert.severity]} text-xs`}
                            >
                              {severityLabels[alert.severity]}
                            </Badge>
                          </div>
                          <p className="font-medium text-sm truncate">{alert.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {alert.message}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAcknowledge(alert.id)}
                          className="shrink-0"
                          data-testid={`acknowledge-${alert.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
